import { X } from '@tamagui/lucide-icons';
import { Avatar } from '@unistocks/components/avatar/avatar';
import { ScrollView } from '@unistocks/components/basic/scrollview';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Logo } from '@unistocks/components/logo/logo';
import { RoutesAccordion } from '@unistocks/features/navigation/routes';
import { useProfileGetQuery } from '@unistocks/proxy/axios-client/ProfileQuery';
import { breakpoints } from '@unistocks/shared/breakpoints';
import { OVERLAY_OPACITY } from '@unistocks/shared/consts';
import { useNavigation, useRouter } from 'expo-router';
import React, { useCallback, useEffect } from 'react';
import {
  AnimatePresence,
  Nav,
  Paragraph,
  Portal,
  Theme,
  ThemeableStack,
  useThemeName
} from 'tamagui';
import { useModalContext } from '../../providers/modal-context';

type DrawerProps = {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export const Drawer: React.FC<DrawerProps> = ({ open, onOpenChange }) => {
  const navigation = useNavigation();
  const router = useRouter();
  useModalContext(open);
  const { data: profile } = useProfileGetQuery();

  useEffect(() => {
    if (open) {
      // TODO: Use instead of onPress if working
      const unsubscribe = navigation.addListener('beforeRemove', () => {
        onOpenChange(false);
      });

      return () => {
        unsubscribe();
      };
    }
  }, [navigation, onOpenChange, open]);

  const onProfilePress = useCallback(() => {
    router.navigate('/(tabs)/(menu)/settings/edit-profile');
    onOpenChange(false);
  }, [router, onOpenChange]);

  return (
    <Portal>
      <PassthroughTheme>
        <AnimatePresence>
          {open ? (
            <ThemeableStack
              key="drawer-overlay"
              fullscreen
              transition={'quick'}
              pointerEvents="auto"
              bg={'black'}
              pos={'absolute'}
              zIndex={100_000 - 1}
              o={OVERLAY_OPACITY}
              enterStyle={{ opacity: 0 }}
              exitStyle={{ opacity: 0 }}
              onPress={() => onOpenChange(false)}
            />
          ) : null}
          {open ? (
            <Column
              key="drawer"
              pointerEvents="auto"
              backgroundColor={'$background'}
              transition={'quick'}
              enterStyle={{ x: '-100%' }}
              exitStyle={{ x: '-100%' }}
              justifyContent="flex-start"
              p={'$4'}
              h={'100%'}
              maxW={breakpoints.xs}
              elevation={'$3'}
              zIndex={100_000}
            >
              <Row w={'100%'} justifyContent={'space-between'} alignItems={'center'}>
                <Logo w={'$14'} />
                <X
                  color={'$headingFont'}
                  strokeWidth={3}
                  size={'$3'}
                  hitSlop={12}
                  cursor="pointer"
                  onPress={() => onOpenChange(false)}
                />
              </Row>

              <Avatar size={'$12'} mx={'auto'} my={'$8'} mb={'$4'} onPress={onProfilePress} />
              <Paragraph size={'$8'} mx={'auto'}>
                {profile?.name} {profile?.surname}
              </Paragraph>

              <Nav mt={'$5'} flex={1}>
                <ScrollView showsVerticalScrollIndicator={false}>
                  <RoutesAccordion withSignOut onPress={() => onOpenChange(false)} />
                </ScrollView>
              </Nav>
            </Column>
          ) : null}
        </AnimatePresence>
      </PassthroughTheme>
    </Portal>
  );
};

const PassthroughTheme = ({
  children,
  passThrough
}: {
  passThrough?: boolean;
  children?: React.ReactNode;
}) => {
  const themeName = useThemeName();

  return (
    <Theme passThrough={passThrough} name={themeName} forceClassName>
      {children}
    </Theme>
  );
};
